import { PlusOutlined } from '@ant-design/icons';
import { useMemoizedFn, useUnmount } from 'ahooks';
import { Button, Input, message, Popconfirm, Select, Spin, Table } from 'antd';
import { TableProps } from 'antd/lib/table';
import { useGetPermission } from 'hooks/business/useNewPermisson';
import useModal from 'hooks/useModal';
import { produce } from 'immer';
import { isNil, uniq } from 'lodash-es';
import { useMemo, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { configureUserRoles } from 'services/api/assistant.account';
import { removeUserFromDepartment } from 'services/api/department/department';
import { errorToasterWithServerMsg } from 'utils/errorToaster';
import AccountStatus from 'views/Assistant/components/AccountStatus';
import {
  accountOrderByStatus,
  excludeRoleList,
  translateRole,
  useRoleMap,
} from 'views/Assistant/utils';
import AssignReceptionConfColumn from '../components/AssignReceptionConfColumn';
import { useDepartmentDetailOfId } from '../hooks/data';
import { AccountInDepartment } from './DepartmentAccountDetail/DepartmentAccountDetailForm';
import DepartmentAccountDetailFormDrawer from './DepartmentAccountDetail/DepartmentAccountDetailFormDrawer';
import RoleDetailFormDrawer from '../RoleDetail/RoleDetailFormDrawer';
import { scopesCannotChange } from 'services/api/department/permissions';
import { getRoleDetail, updateRole } from 'services/api/department/roleWithDepartment';
import ExportExcel from '../StoreConsultationDistribution/ExportExcel';
import styled from '@emotion/styled';
import { isMasterOnline } from 'productLine';
import BatchUpdateDrawer from 'views/Assistant/components/BatchUpdateDrawer';

interface DepartmentDetail extends StoreHomeV2.DepartmentDetail {}

export interface DepartmentAccountTableProps {
  departmentId: DepartmentDetail['id'];
}

const DepartmentAccountTable = (props: DepartmentAccountTableProps) => {
  const { departmentId } = props;
  const { t } = useTranslate();
  const { visible, show, hide, meta: accountModalMeta } = useModal<AccountInDepartment>();
  const roleModal = useModal();
  const [accountKeyword, setAccountKeyword] = useState('');

  const [selectedRoleName, setSelectedRoleName] = useState<string>();

  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const batchUpdateDrawerState = useModal();

  const {
    data: departmentDetail,
    loading,
    mutate,
    refresh,
  } = useDepartmentDetailOfId(departmentId);
  const roleMap = useRoleMap(departmentDetail?.roles);
  const getPermission = useGetPermission();
  const editable = getPermission('帐号管理');
  const deleteUser = useMemoizedFn((userId: number, departmentId?: number) => {
    if (departmentId === undefined) {
      return;
    }
    removeUserFromDepartment(departmentId, userId)
      .then(() => refresh())
      .catch(() => message.error(t('department.帐号移出部门失败，请稍后再试')));
  });
  const columns = useMemo(() => {
    const cols: TableProps<AccountInDepartment>['columns'] = [
      {
        title: t('department.帐号'),
        dataIndex: 'account',
        className: 'break-all',
        render: (_, { account, userName }) => {
          return account || userName || '-';
        },
      },
      {
        title: t('field.name'),
        dataIndex: 'userName',
        className: 'break-all',
        render: (_, { userName }) => userName || '-',
      },
      {
        title: t('field.phone'),
        dataIndex: 'phoneNumber',
        className: 'break-all',
        render: (phoneNumber: AccountInDepartment['phoneNumber']) => phoneNumber || '-',
      },
      {
        title: t('review.状态'),
        className: 'break-all',
        render: (value: Assistant.Account['status'], { sleepFlag }) => {
          return <AccountStatus status={value} sleepFlag={sleepFlag} />;
        },
        dataIndex: 'userStatus',
      },
      {
        title: t('department.角色'),
        dataIndex: 'roleName',
        className: 'break-all',
        render: (roleName) => {
          return translateRole(roleName, t);
        },
      },
      {
        title: t('department.分配模式'),
        className: 'break-all',
        dataIndex: 'assignConversation',
        render: (_, { roleId }) => {
          const role = roleMap.get(roleId);

          if (!role) {
            return '-';
          }

          const { isAdmin, adminStoreScopes, assignConversation, assignConversationConfig } = role;

          return (
            <AssignReceptionConfColumn
              isAdmin={isAdmin}
              adminStoreScopes={adminStoreScopes}
              assignConversation={assignConversation}
              assignConversationConfig={assignConversationConfig}
            />
          );
        },
      },
      ...(editable
        ? [
            {
              title: t('操作'),
              className: 'break-all',
              render: (_: AccountInDepartment['userId'], record: AccountInDepartment) => {
                // 官方角色
                const officialRole = roleMap.get(record.roleId)?.isDefault === 1;

                return (
                  <Operations>
                    {excludeRoleList.some((r) => r === record.roleName) ? (
                      '-'
                    ) : (
                      <>
                        <Button type="link" onClick={() => show(record)}>
                          {t('department.切换角色')}
                        </Button>

                        {!officialRole && (
                          <Button
                            type="link"
                            onClick={async () => {
                              try {
                                const role = await getRoleDetail(record.roleId).then(
                                  (r) => r?.data?.data,
                                );
                                roleModal.show({
                                  initialValues: Object.assign({}, role, {
                                    copyRoleId: record.roleId,
                                  }),
                                  partialMode: true,
                                });
                              } catch (e) {
                                errorToasterWithServerMsg(
                                  e,
                                  t('department.拉取角色数据失败，请稍后再试'),
                                );
                              }
                            }}
                          >
                            {t('department.修改权限')}
                          </Button>
                        )}

                        <Popconfirm
                          title={t(
                            'department.帐号移出后，不再拥有该部门的角色权限，你确认要移出吗？',
                          )}
                          onConfirm={() => deleteUser(record.userId, departmentDetail?.id)}
                        >
                          <Button type="link">{t('department.移出部门')}</Button>
                        </Popconfirm>
                      </>
                    )}
                  </Operations>
                );
              },
            },
          ]
        : []),
    ];
    return cols;
  }, [departmentDetail, editable, show, deleteUser, t, roleMap, roleModal]);
  useUnmount(() => {
    roleMap.current.clear();
  });
  const updateAccount = useMemoizedFn((values: AccountInDepartment) => {
    configureUserRoles({
      userId: values.userId,
      roleIds: [values.roleId],
    })
      .then(() => {
        message.success(t('department.设置帐号角色成功'));
        mutate((detail) => {
          if (!detail?.accounts?.length) {
            return detail;
          }
          const idx = detail.accounts.findIndex((target) => target.userId === values.userId);
          const target = detail.accounts?.[idx];
          if (!target) {
            return detail;
          }
          return produce(detail, (department) => {
            // eslint-disable-next-line no-param-reassign
            department.accounts[idx] = { ...target, ...values };
            return department;
          });
        });
        refresh();
        hide();
      })
      .catch((e) => {
        errorToasterWithServerMsg(e, e?.response?.data?.msg);
      });
  });
  const dataSource = useMemo(() => {
    const accounts =
      accountKeyword || selectedRoleName
        ? departmentDetail?.accounts?.filter(
            ({ account, userName, phoneNumber, roleName }) =>
              (!accountKeyword ||
                account?.includes(accountKeyword) ||
                userName?.includes(accountKeyword) ||
                phoneNumber?.includes(accountKeyword)) &&
              (!selectedRoleName || roleName === selectedRoleName),
          )
        : departmentDetail?.accounts;
    return accountOrderByStatus(accounts, 'userStatus');
  }, [departmentDetail, accountKeyword, selectedRoleName]);

  return (
    <Spin spinning={loading}>
      <div className="flex justify-between items-center mb-3">
        <div>{t('feedback.共x项', { x: departmentDetail?.accounts?.length ?? 0 })}</div>
        <div className="flex gap-2">
          <Select
            style={{ minWidth: 120 }}
            allowClear
            dropdownMatchSelectWidth={false}
            placeholder={t('department.角色')}
            value={selectedRoleName}
            onChange={setSelectedRoleName}
            options={uniq(departmentDetail?.accounts.map((_) => _.roleName)).map((roleName) => {
              return {
                value: roleName,
                label: translateRole(roleName, t),
              };
            })}
          />
          <Input.Search placeholder={t('请输入帐号搜索')} allowClear onSearch={setAccountKeyword} />
          {editable && (
            <>
              {departmentDetail?.id && <ExportExcel departmentId={departmentDetail.id} />}
              {isMasterOnline ? null : (
                <Button
                  onClick={() => {
                    show();
                  }}
                >
                  <PlusOutlined /> {t('department.添加帐号')}
                </Button>
              )}
            </>
          )}
        </div>
      </div>
      <Table
        key={departmentDetail?.id}
        rowKey="userId"
        dataSource={dataSource}
        columns={columns}
        pagination={{ showSizeChanger: true }}
        rowSelection={{
          selectedRowKeys: selectedIds,
          onChange: setSelectedIds as any,
        }}
      />
      {selectedIds.length > 0 && (
        <div className="mt-[-48px]">
          {t('已选x个账号', { count: selectedIds.length })}
          <Button type="link" onClick={batchUpdateDrawerState.show}>
            {t('批量编辑')}
          </Button>
        </div>
      )}
      <BatchUpdateDrawer
        userIds={selectedIds}
        visible={batchUpdateDrawerState.visible}
        onClose={batchUpdateDrawerState.hide}
      />
      {departmentDetail ? (
        <>
          <DepartmentAccountDetailFormDrawer
            destroyOnClose
            title={
              isNil(accountModalMeta?.userId) ? t('department.添加帐号') : t('department.切换角色')
            }
            visible={visible}
            onClose={() => hide()}
            formProps={{
              initialValues: accountModalMeta,
              departmentDetail,
              refreshDepartment: refresh,
              onFinish: updateAccount,
            }}
          />

          {/* 修改角色的权限 */}
          <RoleDetailFormDrawer
            department={departmentDetail}
            title={t('department.角色')}
            destroyOnClose
            formProps={{
              initialValues: (roleModal.meta as any)?.initialValues,
              partialMode: (roleModal.meta as any)?.partialMode,
              onFinish: async (values: any) => {
                const {
                  rolePermScopeType,
                  allScopes = [],
                  permissionWithScopes,
                  assignConversationConfig,
                  ...others
                } = values;

                const submitValue = {
                  ...others,
                  rolePermScopeType,
                  permissionWithScopes:
                    rolePermScopeType === 'DIFF_BY_PERM'
                      ? permissionWithScopes
                      : permissionWithScopes.map((permission: any) => {
                          const { permissionName } = permission;

                          if (scopesCannotChange.includes(permissionName)) {
                            return permission;
                          }

                          return {
                            ...permission,
                            platformStoreScopes: allScopes,
                          };
                        }),
                  assignConversationConfig: {
                    ...assignConversationConfig,
                    platformStoreScopes:
                      rolePermScopeType === 'DIFF_BY_PERM'
                        ? assignConversationConfig.platformStoreScopes
                        : allScopes,
                  },
                };

                try {
                  await updateRole(submitValue);
                  message.success(t('department.已更新角色'));
                  refresh();
                  roleModal.hide();
                } catch (e) {
                  errorToasterWithServerMsg(e);
                }
              },
            }}
            visible={roleModal.visible}
            onClose={() => roleModal.hide()}
          />
        </>
      ) : null}
    </Spin>
  );
};

const Operations = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  > button {
    padding-left: 0;
  }
`;

export default DepartmentAccountTable;
